import { ReactNode, createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react"
import {
    useGetDisponibilidadHabitacionesQuery,
    useGetDisponibilidadTurnosAtencionQuery,
    useListenCheckDisponibilidadHabitacionesSubscription,
    EstadosTurnosAtencion,
    GetDisponibilidadHabitacionesQuery,
    GetDisponibilidadTurnosAtencionQuery,
    DisponibilidadPanelAction,
} from "src/gql/schema"
import { requestWsReconnect } from "src/graphql"
import { useProfile } from "src/shared/hooks/useProfile"

export type DisponibilidadHabitacion = NonNullable<GetDisponibilidadHabitacionesQuery["disponibilidad_habitaciones"]>[number]

export interface ListaEsperaItem {
    nombreHabitacion: string
    folios: string[]
}

type TurnosQuery = GetDisponibilidadTurnosAtencionQuery["turnos_atencion"]
type ListaEsperaAgrupada = Record<string, { nombre: string; folios: string[]; enCurso: string[] }>

interface DisponibilidadContextValue {
    loading: boolean
    habitaciones: DisponibilidadHabitacion[]
    listaEspera: ListaEsperaItem[]
    folioTurno: string
    folioAnuncio: string
}

const DisponibilidadContext = createContext<DisponibilidadContextValue | null>(null)

const ESTADOS_TURNOS = [EstadosTurnosAtencion.EnEspera, EstadosTurnosAtencion.EnCurso]

const foliosPorEstado = (turnos: { estado: EstadosTurnosAtencion; folio_turno?: string | null }[], estado: EstadosTurnosAtencion) =>
    turnos.filter((t) => t.estado === estado && t.folio_turno).map((t) => t.folio_turno as string)

const getFolioNumber = (folio: string): number => {
    const match = folio?.match(/(\d+)/)
    return match ? parseInt(match[1], 10) : 0
}

// La query trae los turnos planos: se agrupan por tipo de habitación.
const agruparTurnosQuery = (turnos: TurnosQuery): ListaEsperaAgrupada => {
    const agrupado: ListaEsperaAgrupada = {}
    turnos.forEach((t) => {
        const id = t.tipo_habitacion_id
        if (!id || !t.folio_turno) return
        if (!agrupado[id]) agrupado[id] = { nombre: t.tipo_habitacion?.nombre ?? "Sin nombre", folios: [], enCurso: [] }
        if (t.estado === EstadosTurnosAtencion.EnEspera) agrupado[id].folios.push(t.folio_turno)
        if (t.estado === EstadosTurnosAtencion.EnCurso) agrupado[id].enCurso.push(t.folio_turno)
    })
    return agrupado
}

const DisponibilidadProvider = ({ children }: { children: ReactNode }) => {
    const { hotel_id } = useProfile()

    const { loading, data, refetch } = useGetDisponibilidadHabitacionesQuery({
        variables: {
            hotel_id,
        },
    })

    const { loading: loadTurnos, data: turnosData, refetch: refetchTurnos } = useGetDisponibilidadTurnosAtencionQuery({
        variables: {
            hotel_id,
            estado: ESTADOS_TURNOS,
        },
    })

    // Las queries solo dan el estado inicial; los cambios llegan por la suscripción y se aplican sobre este estado.
    const [habitaciones, setHabitaciones] = useState<DisponibilidadHabitacion[]>([])
    const [listaEsperaAgrupada, setListaEsperaAgrupada] = useState<ListaEsperaAgrupada>({})
    // Folio que dispara el modal de `Turno`: solo cambia cuando se llama un turno nuevo.
    const [folioAnuncio, setFolioAnuncio] = useState("")
    const foliosAtendidosRef = useRef<Set<string>>(new Set())
    // El estado se copia de las queries en un efecto; hasta entonces no se considera cargado,
    // para que `Turno` no tome como base un folio vacío y luego anuncie el turno inicial.
    const [habitacionesListas, setHabitacionesListas] = useState(false)
    const [turnosListos, setTurnosListos] = useState(false)

    // Solo se anuncia un folio en curso que no se hubiera visto antes (puede haber varios en curso a la vez).
    const anunciarNuevoEnCurso = useCallback((enCurso: string[]) => {
        const nuevo = enCurso.find((folio) => !foliosAtendidosRef.current.has(folio))
        enCurso.forEach((folio) => foliosAtendidosRef.current.add(folio))
        if (nuevo) setFolioAnuncio(nuevo)
    }, [])

    const aplicarHabitaciones = useCallback((value?: GetDisponibilidadHabitacionesQuery) => {
        if (!value?.disponibilidad_habitaciones) return
        setHabitaciones(value.disponibilidad_habitaciones)
        setHabitacionesListas(true)
    }, [])

    const aplicarTurnos = useCallback((value?: GetDisponibilidadTurnosAtencionQuery, inicial = false) => {
        if (!value?.turnos_atencion) return
        setListaEsperaAgrupada(agruparTurnosQuery(value.turnos_atencion))
        const enCurso = foliosPorEstado(value.turnos_atencion, EstadosTurnosAtencion.EnCurso)
        if (inicial) {
            // El turno en curso al entrar se toma como base (Turno lo muestra una vez), no como turno nuevo.
            foliosAtendidosRef.current = new Set(enCurso)
            setFolioAnuncio(enCurso[0] ?? "")
        } else {
            anunciarNuevoEnCurso(enCurso)
        }
        setTurnosListos(true)
    }, [anunciarNuevoEnCurso])

    useEffect(() => {
        aplicarHabitaciones(data)
    }, [data, aplicarHabitaciones])

    const turnosInicialesRef = useRef(false)
    useEffect(() => {
        if (!turnosData?.turnos_atencion) return
        aplicarTurnos(turnosData, !turnosInicialesRef.current)
        turnosInicialesRef.current = true
    }, [turnosData, aplicarTurnos])

    useListenCheckDisponibilidadHabitacionesSubscription({
        variables: { hotel_id },
        fetchPolicy: "no-cache",
        onData({ data: { data: evento } }) {
            // Cada evento trae el tipo de habitación que cambió, completo y con todos sus turnos vigentes.
            const disponibilidad = evento?.checkDisponibilidadHabitaciones?.disponibilidad_habitaciones
            const tipoEvento = disponibilidad?.tipo_habitacion
            if (!disponibilidad || !tipoEvento) return

            const { turnos_atencion, ...tipoHabitacion } = tipoEvento
            setHabitaciones((prev) => {
                const sinTipo = prev.filter((h) => h.tipo_habitacion_id !== tipoHabitacion.tipo_habitacion_id)
                if (disponibilidad.accion === DisponibilidadPanelAction.Remover) {
                    return sinTipo
                }
                const existe = sinTipo.length !== prev.length
                // Actualizar conserva la posición original; agregar lo coloca al final.
                return existe
                    ? prev.map((h) => (h.tipo_habitacion_id === tipoHabitacion.tipo_habitacion_id ? tipoHabitacion : h))
                    : [...prev, tipoHabitacion]
            })

            // La lista de espera de un tipo no depende de que siga disponible: aun con `remover` se toman sus turnos.
            if (!turnos_atencion) return
            const enCurso = foliosPorEstado(turnos_atencion, EstadosTurnosAtencion.EnCurso)
            setListaEsperaAgrupada((prev) => ({
                ...prev,
                [tipoEvento.tipo_habitacion_id]: {
                    nombre: tipoEvento.nombre ?? "Sin nombre",
                    folios: foliosPorEstado(turnos_atencion, EstadosTurnosAtencion.EnEspera),
                    enCurso,
                },
            }))
            anunciarNuevoEnCurso(enCurso)
        },
    })

    useEffect(() => {
        const resync = async (force: boolean) => {
            if (document.visibilityState !== "visible") return
            // Igual que en RoomsProvider: solo se resincroniza si ya pasó el intervalo mínimo o
            // la conexión está caída, y se espera a que la suscripción esté viva antes de refetchear
            // para que un evento recibido durante el refetch no sea pisado por un snapshot viejo.
            const reconnection = requestWsReconnect({ force })
            if (!reconnection) return

            await reconnection
            // Se aplica el resultado directo: si coincide con el caché, `data` no cambia y el efecto no correría.
            const [habitacionesResult, turnosResult] = await Promise.all([refetch(), refetchTurnos()])
            aplicarHabitaciones(habitacionesResult.data)
            aplicarTurnos(turnosResult.data)
        }
        const onVisibilityChange = () => resync(false)
        const onOnline = () => resync(true)
        document.addEventListener("visibilitychange", onVisibilityChange)
        window.addEventListener("online", onOnline)
        return () => {
            document.removeEventListener("visibilitychange", onVisibilityChange)
            window.removeEventListener("online", onOnline)
        }
    }, [refetch, refetchTurnos, aplicarHabitaciones, aplicarTurnos])

    const listaEspera = useMemo(
        () =>
            Object.values(listaEsperaAgrupada)
                .filter((item) => item.folios.length > 0)
                .sort((a, b) => getFolioNumber(a.folios[0]) - getFolioNumber(b.folios[0]))
                .map((item) => ({ nombreHabitacion: item.nombre, folios: item.folios })),
        [listaEsperaAgrupada]
    )

    // Turno en curso a mostrar: el último anunciado mientras siga en curso, si no cualquier otro en curso.
    const folioTurno = useMemo(() => {
        const enCurso = Object.values(listaEsperaAgrupada).flatMap((item) => item.enCurso)
        return enCurso.includes(folioAnuncio) ? folioAnuncio : enCurso[0] ?? ""
    }, [listaEsperaAgrupada, folioAnuncio])

    return (
        <DisponibilidadContext.Provider
            value={{
                loading:
                    loading ||
                    loadTurnos ||
                    (!!data && !habitacionesListas) ||
                    (!!turnosData && !turnosListos),
                habitaciones,
                listaEspera,
                folioTurno,
                folioAnuncio,
            }}
        >
            {children}
        </DisponibilidadContext.Provider>
    )
}

export const useDisponibilidad = (): DisponibilidadContextValue => {
    const context = useContext(DisponibilidadContext)
    if (!context) {
        throw new Error("useDisponibilidad debe usarse dentro de DisponibilidadProvider")
    }
    return context
}

export default DisponibilidadProvider
